import { useEffect, useRef, useState, type ComponentType } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ClipboardCheck,
  FileText,
  Lightbulb,
  Loader2,
  Presentation,
  Search,
  Server,
} from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { getUserPermissions } from "@/lib/rbac";
import { cn } from "@/lib/utils";

interface GlobalSearchResult {
  type:
    | "customer"
    | "assessment"
    | "qbr"
    | "report"
    | "asset"
    | "recommendation";
  id: number;
  label: string;
  sublabel?: string;
  path: string;
}

const MIN_QUERY_LENGTH = 2;

// Display order + presentation for each result group. The order here is the
// order groups appear in the dropdown.
const GROUPS: {
  type: GlobalSearchResult["type"];
  heading: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { type: "customer", heading: "Customers", icon: Building2 },
  { type: "assessment", heading: "Assessments", icon: ClipboardCheck },
  { type: "qbr", heading: "QBRs", icon: Presentation },
  { type: "report", heading: "Reports", icon: FileText },
  { type: "asset", heading: "Assets", icon: Server },
  { type: "recommendation", heading: "Recommendations", icon: Lightbulb },
];

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function GlobalSearch() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // The groups a user can actually search depend on their role: QBRs and
  // recommendations are vCIO+ only (mirrors the backend gate), so we hide those
  // from the "what you can search" hint for lower roles to avoid promising
  // results they'll never see.
  const canViewQbrs = getUserPermissions(user).canViewQbrs;
  const visibleGroups = canViewQbrs
    ? GROUPS
    : GROUPS.filter((g) => g.type !== "qbr" && g.type !== "recommendation");
  const placeholder = canViewQbrs
    ? "Search customers, QBRs, assets..."
    : "Search customers, assessments, assets...";

  const debounced = useDebouncedValue(query, 250);
  const term = debounced.trim();
  const enabled = term.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, isError } = useQuery<GlobalSearchResult[]>({
    queryKey: ["/api/search", term],
    queryFn: () => apiRequest("GET", `/api/search?q=${encodeURIComponent(term)}`),
    enabled: enabled && open,
    staleTime: 30_000,
  });

  // Guard against a non-array payload. apiRequest falls back to `{ success: true }`
  // for any 2xx non-JSON response (e.g. the SPA HTML served for a brief window
  // right after a deploy/restart, before the /api/search route is live again).
  // Without this guard, `results.filter` throws and takes down the whole app.
  const results: GlobalSearchResult[] = Array.isArray(data) ? data : [];

  // Cmd/Ctrl+K focuses (desktop) or opens (mobile) the search.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        if (!isMobile) {
          // Defer so the input is mounted/visible before focusing.
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile]);

  // Close the desktop dropdown when clicking outside of it.
  useEffect(() => {
    if (isMobile) return;
    const onMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isMobile]);

  const handleSelect = (path: string) => {
    setOpen(false);
    setQuery("");
    setLocation(path);
  };

  const renderResults = () => {
    if (!enabled) {
      return (
        <div className="px-2 py-3">
          <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">
            Search across
          </p>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            {visibleGroups.map((group) => {
              const Icon = group.icon;
              return (
                <div
                  key={group.type}
                  className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground"
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{group.heading}</span>
                </div>
              );
            })}
          </div>
          <p className="px-2 pt-2 text-xs text-muted-foreground">
            Type at least {MIN_QUERY_LENGTH} characters by name or title to begin.
          </p>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="py-6 text-center text-sm text-muted-foreground">
          Search failed. Please try again.
        </div>
      );
    }

    if (isFetching && results.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching...
        </div>
      );
    }

    return (
      <>
        <CommandEmpty>No results found.</CommandEmpty>
        {GROUPS.map((group) => {
          const items = results.filter((r) => r.type === group.type);
          if (items.length === 0) return null;
          const Icon = group.icon;
          return (
            <CommandGroup key={group.type} heading={group.heading}>
              {items.map((result) => (
                <CommandItem
                  key={`${result.type}-${result.id}`}
                  value={`${result.type}-${result.id}`}
                  onSelect={() => handleSelect(result.path)}
                  className="flex items-center gap-2"
                  data-testid={`search-result-${result.type}-${result.id}`}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{result.label}</span>
                  {result.sublabel && (
                    <span className="ml-2 max-w-[40%] truncate text-xs text-muted-foreground">
                      {result.sublabel}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </>
    );
  };

  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Search"
          title={placeholder}
          onClick={() => setOpen(true)}
          data-testid="button-global-search"
        >
          <Search className="h-5 w-5" />
        </Button>
        <Dialog
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setQuery("");
          }}
        >
          <DialogContent className="top-[12%] translate-y-0 overflow-hidden p-0 shadow-lg">
            <VisuallyHidden>
              <DialogTitle>Search</DialogTitle>
            </VisuallyHidden>
            <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2">
              <CommandInput
                autoFocus
                placeholder={placeholder}
                value={query}
                onValueChange={setQuery}
                data-testid="input-global-search"
              />
              <CommandList className="max-h-[60vh]">{renderResults()}</CommandList>
            </Command>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <Command
        shouldFilter={false}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        className={cn(
          "overflow-visible rounded-md border bg-background",
          "[&_[cmdk-input-wrapper]]:border-b-0",
        )}
      >
        <CommandInput
          ref={inputRef}
          placeholder={placeholder}
          value={query}
          onValueChange={setQuery}
          onFocus={() => setOpen(true)}
          className="pr-20"
          data-testid="input-global-search"
        />
        {!query && (
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 select-none items-center gap-1 rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground lg:flex">
            Ctrl / ⌘ K
          </kbd>
        )}
        {open && (
          <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-md border bg-popover shadow-md">
            <CommandList className="max-h-[60vh]">{renderResults()}</CommandList>
          </div>
        )}
      </Command>
    </div>
  );
}
